import { css } from 'aphrodite';
import * as React from 'react';
import { Fragment, useCallback, useMemo } from 'react';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownItem,
	DropdownSelectTrigger,
	DropdownSeparator,
} from '../../../../../web/components-v2/Dropdown';
import { ISelectOption } from '../../../../../web/components/DeprecatedSelect';
import { bs } from '../../../../../web/styles/styles';
import { useTeamAutocompleteQuery } from '../../../../queries/Aida/useTeamAutocomplete';
import { styleSheet } from '../OutcomeSelect/styles';

interface IProps {
	onChange?: (item: ISelectOption<string>[]) => void;
	selectedTeams?: string[];
}

export const TeamMultiSelect: React.FC<IProps> = ({ onChange, selectedTeams = [] }) => {
	// Use React Query to fetch teams
	const {
		data: teams = [],
		isPending,
		isError,
	} = useTeamAutocompleteQuery({
		enabled: true,
	});

	// Memoize team options to avoid unnecessary re-renders
	const teamOptions = useMemo(() => {
		if (isPending || isError) return [];

		// Filter teams that contain "sdr" (case-insensitive)
		const sdrTeams = teams.filter(team => team.toLowerCase().includes('sdr'));
		const allSdrsSelected = sdrTeams.length > 0 && sdrTeams.every(team => selectedTeams.includes(team));

		const options: ISelectOption<string>[] = teams.map(team => ({
			checked: selectedTeams.includes(team),
			dataContext: team,
			id: team,
			name: team,
			text: team,
		}));

		// Add "All SDRs" helper option at the beginning if there are SDR teams
		if (sdrTeams.length > 0) {
			options.unshift({
				checked: allSdrsSelected,
				dataContext: 'all-sdrs',
				id: 'all-sdrs',
				name: 'all-sdrs',
				text: `All SDRs (${sdrTeams.length})`,
			});
		}

		return options;
	}, [teams, selectedTeams, isPending, isError]);

	// Hooks for v2 functionality - must be at top level to avoid conditional calling
	const handleTeamToggle = useCallback(
		(teamId: string) => {
			const isAllSdrs = teamId === 'all-sdrs';

			// Create updated options based on current teamOptions
			const updatedOptions = teamOptions.map(option => ({ ...option })); // Clone to avoid mutation

			if (isAllSdrs) {
				// If "All SDRs" is clicked, toggle all SDR teams
				const sdrTeams = teams.filter(team => team.toLowerCase().includes('sdr'));
				const allSdrsOption = updatedOptions.find(opt => opt.id === 'all-sdrs');
				const newCheckedState = !allSdrsOption?.checked;

				updatedOptions.forEach(option => {
					if (sdrTeams.includes(option.dataContext)) {
						option.checked = newCheckedState;
					}
				});

				// Update the "All SDRs" option itself
				if (allSdrsOption) {
					allSdrsOption.checked = newCheckedState;
				}
			} else {
				// If specific team is clicked, update that option
				const targetOption = updatedOptions.find(opt => opt.id === teamId);
				if (targetOption) {
					targetOption.checked = !targetOption.checked;
				}

				// Update "All SDRs" checkbox based on SDR team selections
				const sdrTeams = teams.filter(team => team.toLowerCase().includes('sdr'));
				const checkedSdrTeams = updatedOptions.filter(opt => sdrTeams.includes(opt.dataContext) && opt.checked);
				const allSdrsOption = updatedOptions.find(opt => opt.id === 'all-sdrs');

				if (allSdrsOption && sdrTeams.length > 0) {
					allSdrsOption.checked = checkedSdrTeams.length === sdrTeams.length;
				}
			}

			onChange?.(updatedOptions);
		},
		[teamOptions, teams, onChange]
	);

	const handleClearAll = useCallback(() => {
		const clearedOptions = teamOptions.map(option => ({ ...option, checked: false }));
		onChange?.(clearedOptions);
	}, [teamOptions, onChange]);

	const checkedTeamsCount = teamOptions.filter(option => option.checked && option.id !== 'all-sdrs').length;
	const dropdownMessage = isPending
		? 'Loading teams...'
		: isError
			? 'Error loading teams'
			: checkedTeamsCount === 0
				? 'Select teams...'
				: `${checkedTeamsCount} team${checkedTeamsCount === 1 ? '' : 's'} selected`;

	return (
		<Dropdown>
			<DropdownSelectTrigger styles={[bs.wFull]}>{dropdownMessage}</DropdownSelectTrigger>
			<DropdownContent>
				{!isPending &&
					!isError &&
					teamOptions.map(option => {
						const isHelper = option.id === 'all-sdrs';
						return (
							<Fragment key={option.id}>
								<DropdownCheckboxItem
									checked={option.checked}
									onCheckedChange={() => handleTeamToggle(option.id)}
									styles={isHelper ? [styleSheet.helperCheckboxItem] : []}
								>
									{option.text}
								</DropdownCheckboxItem>
								{isHelper && <div className={css(styleSheet.helperSpacing)} />}
							</Fragment>
						);
					})}
				{checkedTeamsCount > 0 && (
					<>
						<DropdownSeparator />
						<DropdownItem onClick={handleClearAll}>Clear all</DropdownItem>
					</>
				)}
			</DropdownContent>
		</Dropdown>
	);
};
